// Local-first writes: every change lands in IndexedDB at once (client uuid) and is queued for the server.
import { uuid } from '@/lib/format';
import { store, storeUserId, type EntryInput, type LocalEntry, type QueueItem } from './db';
import { requestSync } from './sync';
import { findOverlap } from '../overlap';

/** How long a delete waits before it is pushed, so "Undo" can take it back without a server round trip. */
export const UNDO_MS = 6_000;

async function enqueue(babyId: string, id: string, op: QueueItem['op'], notBefore = 0) {
  const db = store();
  const prev = await db.queue.get(id);
  await db.queue.put({
    id,
    babyId,
    op,
    queuedAt: prev?.queuedAt ?? Date.now(),
    notBefore,
    prevOp: op === 'delete' ? (prev?.op === 'upsert' ? 'upsert' : null) : null,
  });
}

/** Create an entry. Returns the local copy (rev 0 until the server confirms it). */
export async function addEntry(babyId: string, input: EntryInput & { id?: string }): Promise<LocalEntry> {
  const db = store();
  const now = new Date().toISOString();
  const me = storeUserId();
  const e = {
    id: input.id ?? uuid(),
    babyId,
    type: input.type,
    startAt: input.startAt,
    endAt: input.endAt ?? null,
    data: input.data,
    note: input.note ?? null,
    categoryId: input.categoryId ?? null,
    rev: 0,
    createdById: me,
    updatedById: me,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    dirty: 1,
    lv: 1,
    base: null,
    sent: 0,
  } as LocalEntry;
  await db.transaction('rw', db.entries, db.queue, async () => {
    await db.entries.put(e);
    await enqueue(babyId, e.id, 'upsert');
  });
  requestSync();
  return e;
}

export type EntryPatch = Partial<Pick<LocalEntry, 'startAt' | 'endAt' | 'note' | 'categoryId'>> & { data?: Record<string, unknown> };

/** Change an entry (data is replaced as a whole). Pushed with baseRev = the last server revision we saw. */
export async function updateEntry(id: string, patch: EntryPatch): Promise<LocalEntry | null> {
  const db = store();
  let out: LocalEntry | null = null;
  await db.transaction('rw', db.entries, db.queue, async () => {
    const cur = await db.entries.get(id);
    if (!cur || cur.deletedAt) return;
    out = { ...cur, ...patch, updatedById: storeUserId(), updatedAt: new Date().toISOString(), dirty: 1, lv: cur.lv + 1 } as LocalEntry;
    await db.entries.put(out);
    await enqueue(cur.babyId, id, 'upsert');
  });
  requestSync();
  return out;
}

/** Stop a running timer. A breastfeed also closes its open side. */
export async function stopTimer(id: string, endAt = new Date().toISOString()) {
  const cur = await store().entries.get(id);
  if (!cur || cur.endAt) return null;
  if (cur.type === 'breast') {
    const segments = cur.data.segments.map((s) => (s.endAt ? s : { ...s, endAt }));
    return updateEntry(id, { endAt, data: { ...cur.data, segments } });
  }
  return updateEntry(id, { endAt });
}

/** Soft delete. The entry disappears at once; the server hears about it after the undo window. */
export async function removeEntry(id: string): Promise<LocalEntry | null> {
  const db = store();
  let snapshot: LocalEntry | null = null;
  await db.transaction('rw', db.entries, db.queue, async () => {
    const cur = await db.entries.get(id);
    if (!cur || cur.deletedAt) return;
    snapshot = cur;
    await db.entries.put({ ...cur, deletedAt: new Date().toISOString(), dirty: 1, lv: cur.lv + 1 });
    await enqueue(cur.babyId, id, 'delete', Date.now() + UNDO_MS);
  });
  requestSync(UNDO_MS + 100);
  return snapshot;
}

/** Another sleep of this baby (on this phone) that the given times would overlap, or null. */
export async function sleepOverlap(babyId: string, startAt: string, endAt: string | null, exceptId?: string): Promise<LocalEntry | null> {
  const lo = new Date(Date.parse(startAt) - 26 * 3600_000).toISOString();
  const hi = endAt ?? new Date(Math.max(Date.now(), Date.parse(startAt))).toISOString();
  const rows = await store().entries.where('[babyId+startAt]').between([babyId, lo], [babyId, hi], true, true).toArray();
  return findOverlap({ startAt, endAt }, rows.filter((e) => e.type === 'sleep' && !e.deletedAt && e.id !== exceptId));
}

/** Undo a delete. If the delete already reached the server (deletion wins there), the entry comes back under a new id. */
export async function undoRemove(snapshot: LocalEntry): Promise<void> {
  const db = store();
  let recreate = false;
  await db.transaction('rw', db.entries, db.queue, async () => {
    const q = await db.queue.get(snapshot.id);
    const cur = await db.entries.get(snapshot.id);
    if (q?.op === 'delete' && cur) {
      await db.entries.put({ ...cur, deletedAt: null, lv: cur.lv + 1, dirty: q.prevOp ? 1 : 0 });
      if (q.prevOp) await db.queue.put({ ...q, op: q.prevOp, notBefore: 0, prevOp: null });
      else await db.queue.delete(q.id);
    } else {
      recreate = true;
    }
  });
  if (recreate) {
    await addEntry(snapshot.babyId, { type: snapshot.type, startAt: snapshot.startAt, endAt: snapshot.endAt, data: snapshot.data as Record<string, unknown>, note: snapshot.note, categoryId: snapshot.categoryId });
  }
  requestSync();
}
